// Pure props builder for `blog_link_clicked` (D-16). The capture itself lives in the shared link
// primitives (BlogAnchor, and later BlogTrustLink), which call trackBlog.linkClicked with this
// output. Nothing here touches posthog-js or a browser global.
//
// No PII: props are the surface, the classified target, the post slug and storeEventProps only.

import { storeEventProps } from "../posthog";
import type { StoreEventProps, TrackableStore } from "../posthog";
import { classifyBlogHref } from "./links";
import type { BlogTargetType } from "./links";

/** Where a blog link was clicked (UI-SPEC section 6 + amendment 7). */
export type BlogLinkSurface = "blog_post" | "blog_index" | "store_page" | "category_page";

export type BlogLinkEventProps = StoreEventProps & {
  surface: BlogLinkSurface;
  target_type: BlogTargetType;
  target_slug: string | null;
  post_slug?: string;
};

/**
 * Returns null for hrefs that are not an internal store/category/blog/search target (external
 * links, hashes, mailto): those fire no event. `post_slug` is present only when a postSlug is
 * passed, so store_page / category_page clicks carry no such key at all. Store props are added
 * on store targets and on the store_page surface (the page's store), always via storeEventProps
 * (never inlined).
 */
export const blogLinkEventProps = ({
  href,
  surface,
  postSlug,
  store,
}: {
  href: string;
  surface: BlogLinkSurface;
  postSlug?: string;
  store?: TrackableStore | null;
}): BlogLinkEventProps | null => {
  const target = classifyBlogHref(href);
  if (!target) return null;

  return {
    surface,
    target_type: target.targetType,
    target_slug: target.targetSlug,
    ...(postSlug ? { post_slug: postSlug } : {}),
    // On the store page the link's store is the page's store (UI-SPEC section 6), whatever the
    // target; everywhere else store props ride only on store targets.
    ...(target.targetType === "store" || surface === "store_page" ? storeEventProps(store) : {}),
  };
};
